import {IvrFieldType} from "@/types/SClassTest.ts";

export interface TypeFieldConfig {
    optionRules: boolean;
    optionRulesLabel: string;
    maxMinDigits: boolean;
    correctOptions: boolean;
    correctOptionsLabel: string;
    correctOptionsPlaceholder: string;
    score: boolean;
    toConfirm: boolean;
    loop: boolean;
    timeout: boolean;
    timeoutLabel: string;
    gotoOptions: boolean;
    correctFailedSounds: boolean;
    afterSound: boolean;
    sttLanguage: boolean;
    geminiSettings: boolean;
    advancedModulesHint: string;
}

const BASE: TypeFieldConfig = {
    optionRules: false,
    optionRulesLabel: 'אפשרויות הקשה (לדוגמה: 1234)',
    maxMinDigits: false,
    correctOptions: false,
    correctOptionsLabel: 'תשובות נכונות',
    correctOptionsPlaceholder: 'הקלד תשובה ולחץ Enter להוספה',
    score: true,
    toConfirm: true,
    loop: true,
    timeout: true,
    timeoutLabel: 'משך זמן (שניות)',
    gotoOptions: true,
    correctFailedSounds: true,
    afterSound: true,
    sttLanguage: false,
    geminiSettings: false,
    advancedModulesHint: 'ניתן להשתמש ב: skip_if_incorrect, send_zintuk=<יעד>, zintuk_to_sclass=<מזהה שלוחה>',
};

/** מחזיר אילו שדות רלוונטיים להצגה, בהתאם לסוג השאלה שנבחר */
export function getTypeFieldConfig(type: IvrFieldType): TypeFieldConfig {
    switch (type) {
        case 'DIG':
            return {
                ...BASE, optionRules: true, correctOptions: true,
                correctOptionsLabel: 'תשובות נכונות',
                correctOptionsPlaceholder: 'לדוגמה: 1 (ניתן להוסיף כמה תשובות)',
            };
        case 'SEL':
            return {
                ...BASE, optionRules: true, correctOptions: true,
                correctOptionsLabel: 'תשובות נכונות (ניתן גם בפורמט מתקדם: 1:נכון:5)',
                correctOptionsPlaceholder: 'לדוגמה: 1 או 1:נכון:5',
            };
        case 'MSEL':
            return {
                ...BASE, maxMinDigits: true, correctOptions: true,
                correctOptionsLabel: 'תשובות נכונות (ניתן גם בפורמט מתקדם: 1:נכון:5)',
                correctOptionsPlaceholder: 'לדוגמה: 1 או 1:נכון:5',
            };
        case 'INT':
        case 'AMT':
            return {
                ...BASE, maxMinDigits: true, correctOptions: true,
                correctOptionsLabel: 'תשובות נכונות',
                correctOptionsPlaceholder: 'לדוגמה: 100 (ניתן להוסיף כמה תשובות)',
                advancedModulesHint: 'ניתן להשתמש ב: tz (תעודת זהות), valid_hour (שעה), valid_date4/valid_date6/valid_date8 (תאריך), skip_if_incorrect',
            };
        case 'REC':
            return {...BASE, score: false, gotoOptions: false, correctFailedSounds: false, loop: false};
        case 'STT':
            return {
                ...BASE, correctOptions: true, correctOptionsLabel: 'מילות מפתח לזיהוי תשובה נכונה',
                correctOptionsPlaceholder: 'לדוגמה: כן (ניתן להוסיף כמה מילות מפתח)',
                toConfirm: false, loop: false, sttLanguage: true,
            };
        case 'GSTT':
            return {
                ...BASE, correctOptions: true, correctOptionsLabel: 'מילות מפתח לזיהוי תשובה נכונה',
                correctOptionsPlaceholder: 'לדוגמה: כן (ניתן להוסיף כמה מילות מפתח)',
                toConfirm: false, loop: false, geminiSettings: true,
            };
        case 'SC':
            return {
                ...BASE, optionRules: true, score: false,
                optionRulesLabel: 'מקסימום ספרות לניקוד',
            };
        case 'RZ':
            return {
                ...BASE, optionRules: true, score: false, toConfirm: false, loop: false,
                advancedModulesHint: 'ניתן להשתמש ב: send_zintuk=<יעד>, zintuk_to_sclass=<מזהה שלוחה>',
            };
        case 'EML':
            return {
                ...BASE, score: false, toConfirm: false, loop: false, timeout: false,
                correctFailedSounds: false, afterSound: false, gotoOptions: true,
            };
        default:
            return BASE;
    }
}

/** אפשרויות שפה עבור זיהוי דיבור Gemini AI - ברירת מחדל: ללא הגדרת שפה */
export const GEMINI_LANGUAGE_OPTIONS: { value: string; label: string }[] = [
    {value: '', label: 'ללא הגדרת שפה (ברירת מחדל)'},
    {value: 'he', label: 'עברית'},
    {value: 'en', label: 'אנגלית'},
    {value: 'ar', label: 'ערבית'},
    {value: 'ru', label: 'רוסית'},
    {value: 'fr', label: 'צרפתית'},
];

/** אפשרויות מודל Gemini AI */
export const GEMINI_MODEL_OPTIONS: { value: string; label: string }[] = [
    {value: 'gemini-flash-latest', label: 'Gemini Flash (גרסה עדכנית, מומלץ)'},
    {value: 'gemini-flash-lite-latest', label: 'Gemini Flash-Lite (קל ומהיר)'},
    {value: 'gemini-pro-latest', label: 'Gemini Pro (דיוק גבוה)'},
    {value: 'gemini-3.7-flash', label: 'Gemini 3.7 Flash'},
    {value: 'gemini-3.6-flash', label: 'Gemini 3.6 Flash'},
    {value: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash'},
    {value: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash-Lite'},
    {value: 'gemini-3.1-pro', label: 'Gemini 3.1 Pro'},
    {value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash (ישן - עלול שלא לפעול עבור API Key חדש)'},
    {value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro (ישן - עלול שלא לפעול עבור API Key חדש)'},
];
